import React from 'react'
import * as ROUTES from 'constants/routes'
import { Flex, Text, Link, Button, Icon } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { VndlyWorkOrderIssuesItem } from 'api/admin/vndly/listVndlyWorkOrderIssues'
import { useVndlyWorkOrderIssuesContextValue } from '../context/VndlyWorkOrderIssuesProvider'
import { useVndlyIssuesContextValue } from 'pages/VndlyIssues/context/VndlyIssuesProvider'
import { ExternalLinkIcon } from 'lucide-react'
import { formatInTimeZone } from 'date-fns-tz'
import { PaginatedTable } from 'components/Table/PaginatedTable'

const VNDLY_SHIFTS_DEFAULT_TIMEZONE = 'America/New_York' as const

const InstanceCell = ({ instanceId }: { instanceId: number }) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instance = instances.find((instance) => instance.id === instanceId)

  return <Text>{instance?.instanceUrl}</Text>
}

const CompanyCell = ({
  instanceId,
  companyId,
}: {
  instanceId: number
  companyId: number
}) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const company = instances
    .find((instance) => instance.id === instanceId)
    ?.companies.find((company) => company.id === companyId)

  if (!company) {
    return <Text>(unknown)</Text>
  }

  return (
    <Text>
      {company.name} (
      <Link
        to={ROUTES.COMPANY_DETAIL.replace(':companyId', company.id.toString())}
        target="_blank"
      >
        {company.id}
      </Link>
      )
    </Text>
  )
}

function buildVndlyWorkOrderSearchUrl(
  instanceUrl: string,
  workerName: string,
  jobTitle: string
): string {
  const baseUrl = instanceUrl.replace(/\/$/, '')
  const params = new URLSearchParams({
    search: `${workerName} ${jobTitle}`,
  })

  return `${baseUrl}/work_orders/all?${params.toString()}`
}

const SearchVndlyWorkOrderButton = ({
  instanceId,
  workerName,
  jobTitle,
}: {
  instanceId: number
  workerName: string
  jobTitle: string
}) => {
  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instanceUrl = instances.find(
    (instance) => instance.id === instanceId
  )?.instanceUrl

  if (instanceUrl === undefined) {
    return null
  }

  return (
    <Button
      kind="small"
      variant="secondary"
      as="a"
      href={buildVndlyWorkOrderSearchUrl(instanceUrl, workerName, jobTitle)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ padding: '0 0.5rem', textDecoration: 'none' }}
    >
      <Flex alignItems="center" gap={1}>
        <Text fontSize="12px">Search in VNDLY</Text>
        <Icon icon={ExternalLinkIcon} size={12} />
      </Flex>
    </Button>
  )
}

const columns: ColumnDef<VndlyWorkOrderIssuesItem>[] = [
  {
    header: 'Instance',
    accessorKey: 'instanceId',
    cell: ({ row }) => <InstanceCell instanceId={row.original.instanceId} />,
  },
  {
    header: 'Company',
    accessorKey: 'companyId',
    cell: ({ row }) => (
      <CompanyCell
        instanceId={row.original.instanceId}
        companyId={row.original.companyId}
      />
    ),
  },
  {
    header: 'Shift ID',
    accessorKey: 'shiftId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.SHIFT_DETAIL.replace(
          ':shiftId',
          row.original.shiftId.toString()
        )}
        target="_blank"
      >
        {row.original.shiftId}
      </Link>
    ),
  },
  {
    header: 'Worker ID',
    accessorKey: 'workerId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.USERS.replace(':keyword', row.original.workerId.toString())}
        target="_blank"
      >
        {row.original.workerId}
      </Link>
    ),
  },
  {
    header: 'Shift Starts At',
    accessorKey: 'shiftStartsAt',
    cell: ({ row }) => {
      const shiftStartsAt = formatInTimeZone(
        row.original.shiftStartsAt,
        VNDLY_SHIFTS_DEFAULT_TIMEZONE,
        'EEEE, MMMM d, h:mm a z'
      )
      return <Text>{shiftStartsAt}</Text>
    },
  },
  {
    header: 'Worker Name',
    accessorKey: 'workerName',
    cell: ({ row }) => <Text>{row.original.workerName}</Text>,
  },
  {
    header: 'VNDLY Job Title',
    accessorKey: 'vndlyJobTitle',
    cell: ({ row }) => <Text>{row.original.vndlyJobTitle ?? '-'}</Text>,
  },
  {
    header: 'Issues',
    cell: ({ row }) => {
      return (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {row.original.issues.join(', ')}
        </Text>
      )
    },
  },
  {
    header: 'Actions',
    cell: ({ row }) => {
      return (
        <Flex gap={2} flexWrap="wrap">
          <SearchVndlyWorkOrderButton
            instanceId={row.original.instanceId}
            workerName={row.original.workerName}
            jobTitle={row.original.vndlyJobTitle ?? ''}
          />
        </Flex>
      )
    },
  },
]

export const VndlyWorkOrderIssuesMain = () => {
  const {
    state: { data, pagination, isFetching },
    actions: { setPagination },
  } = useVndlyWorkOrderIssuesContextValue()

  return (
    <PaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No work order issues found."
      emptyStateHeight={50}
    />
  )
}
